import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import DocumentPreview from "../DocumentPreview";
import "./multiDocumentPreview.scss";

export default function MultiDocumentPreviewDock({
  previews,
  onRemove,
  onCloseAll,
}) {
  const [maximizedId, setMaximizedId] = useState(null);

  useEffect(() => {
    if (maximizedId && !previews.some((p) => p.id === maximizedId)) {
      setMaximizedId(null);
    }
  }, [previews, maximizedId]);

  useEffect(() => {
    if (maximizedId) {
      document.body.classList.add("doc-preview-dock-maximized");
    } else {
      document.body.classList.remove("doc-preview-dock-maximized");
    }
    return () => document.body.classList.remove("doc-preview-dock-maximized");
  }, [maximizedId]);

  if (!previews?.length) return null;

  const count = previews.length;
  const isMaximized = Boolean(maximizedId);

  const handleCloseAll = () => {
    setMaximizedId(null);
    onCloseAll();
  };

  return (
    <div
      className={`doc-multi-preview-dock${
        isMaximized ? " doc-multi-preview-dock--maximized" : ""
      }`}
      role="region"
      aria-label="Document comparison"
    >
      <div className="doc-multi-preview-dock-header">
        <div className="doc-multi-preview-dock-title">
          <span className="doc-multi-preview-dock-heading">
            {isMaximized
              ? previews.find((p) => p.id === maximizedId)?.documentName ||
                "Document preview"
              : count === 1
                ? "Document preview"
                : `Comparing ${count} documents`}
          </span>
          <span className="doc-multi-preview-dock-hint">
            {isMaximized
              ? "Use restore to return to side-by-side comparison"
              : count > 1
                ? "Scroll horizontally to see all open documents"
                : "Open more files from the list to compare side by side"}
          </span>
        </div>
        <button
          type="button"
          className="doc-multi-preview-close-all"
          onClick={handleCloseAll}
        >
          Close all
        </button>
      </div>
      <div
        className={`doc-multi-preview-grid${
          count === 1 ? " doc-multi-preview-grid--single" : ""
        }${isMaximized ? " doc-multi-preview-grid--maximized" : ""}`}
      >
        {previews.map((pane) => (
          <div
            key={pane.id}
            className={`doc-multi-preview-pane${
              maximizedId === pane.id ? " is-maximized" : ""
            }${isMaximized && maximizedId !== pane.id ? " is-hidden" : ""}`}
          >
            <div className="doc-multi-preview-pane-header">
              <span className="doc-multi-preview-pane-name" title={pane.documentName}>
                {pane.documentName || "Document"}
              </span>
              <div className="doc-multi-preview-pane-actions">
                {maximizedId === pane.id ? (
                  <button
                    type="button"
                    className="doc-multi-preview-pane-icon-btn"
                    onClick={() => setMaximizedId(null)}
                    aria-label={`Restore ${pane.documentName || "document"} to comparison view`}
                    title="Restore"
                  >
                    <FullscreenExitIcon fontSize="small" />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="doc-multi-preview-pane-icon-btn"
                    onClick={() => setMaximizedId(pane.id)}
                    aria-label={`Maximize ${pane.documentName || "document"}`}
                    title="Maximize"
                  >
                    <FullscreenIcon fontSize="small" />
                  </button>
                )}
                <button
                  type="button"
                  className="doc-multi-preview-pane-close"
                  onClick={() => {
                    if (maximizedId === pane.id) setMaximizedId(null);
                    onRemove(pane.id);
                  }}
                  aria-label={`Close ${pane.documentName || "document"}`}
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="doc-multi-preview-pane-body">
              <DocumentPreview
                blobUrl={pane.blobUrl}
                documentType={pane.documentType}
                documentName={pane.documentName}
                className="doc-preview-in-dock"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

MultiDocumentPreviewDock.propTypes = {
  previews: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      blobUrl: PropTypes.string,
      documentType: PropTypes.string,
      documentName: PropTypes.string,
    }),
  ),
  onRemove: PropTypes.func.isRequired,
  onCloseAll: PropTypes.func.isRequired,
};
